/**
 * Translates the page's in-memory results into the shape the runs API stores.
 *
 * Kept separate from the page so the mapping and the checkpoint cadence can be tested
 * without a browser, and so the stored vocabulary can never quietly drift from the
 * vocabulary shown on screen.
 */

import type { BulkRunRow } from "../../../api/addressBook.service.js";
import { outcomeGroup, type RowResult } from "./runUpload.js";

/** Rows written per checkpoint. Small enough to bound loss, large enough to be cheap. */
export const CHECKPOINT_EVERY = 100;

export function toRunRows(results: RowResult[]): BulkRunRow[] {
  return results.map(result => ({
    line_number: result.row.lineNumber,
    company_uuid: result.row.companyUuid,
    company_name: result.row.label,
    reference_value: result.row.referenceValue,
    outcome: result.outcome,
    http_status: result.status,
    detail: result.detail,
  }));
}

/**
 * Rebuilds results from a stored run so a saved run renders through the same
 * ResultsTable as a live one. The plan fields are reconstructed rather than stored —
 * nothing downstream of the table reads them, and storing them would invite the two
 * representations to drift.
 */
export function fromRunRows(rows: BulkRunRow[]): RowResult[] {
  return rows.map(row => ({
    row: {
      lineNumber: row.line_number,
      companyUuid: row.company_uuid,
      referenceValue: row.reference_value,
      label: row.company_name,
      problem: row.outcome === "malformed" ? "invalid-uuid" : null,
      plan: row.outcome === "created" ? "write" : "already-mapped",
      existingValue: null,
    },
    outcome: row.outcome as RowResult["outcome"],
    status: row.http_status,
    detail: row.detail,
  }));
}

export function countsFor(results: RowResult[]): {
  created_count: number;
  skipped_count: number;
  failed_count: number;
} {
  const tally = { created: 0, skipped: 0, failed: 0 };
  for (const result of results) {
    tally[outcomeGroup(result.outcome)] += 1;
  }
  return {
    created_count: tally.created,
    skipped_count: tally.skipped,
    failed_count: tally.failed,
  };
}

/**
 * True when this row count should trigger a checkpoint: on every CHECKPOINT_EVERY-th
 * row, and on the last one. The final report is sent separately, so the last-row
 * checkpoint matters only for a run that dies immediately after finishing the writes.
 */
export function shouldCheckpoint(done: number, total: number): boolean {
  return done > 0 && (done % CHECKPOINT_EVERY === 0 || done === total);
}
